/**
 * Current VLANs on a switch port -- types for GET /devices/{serial}/switch/port-vlans and the pure
 * logic the port panel uses to show them and to edit them safely.
 *
 * WHY (owner, 2026-10-07, repeated): the port panel said "VLAN 1" for JH-MDF port 1, the trunk
 * to JH-GW-01 carrying 400/1000/1050/1051/1254 tagged. "When you try to modify a port, you should
 * be able to see the current VLANs so that you know what to add and/or remove." The switch's
 * own state report carries only the untagged VLAN (PVID); the tagged list comes from the pushed
 * config or Meridian's own recorded writes, and the controller says which (services/
 * switch_port_vlans). This module never invents membership: an unknown tagged list is
 * "not recorded", never "none".
 *
 * EDITING: the old box was a single "Access VLAN" field that, typed on a trunk, turned it into
 * an access port without saying so. Here the edit starts FROM the current VLANs, and
 * buildVlanChange() emits only the delta (tagged added, tagged removed, native change), so the
 * preview can state exactly what changes. Pure and dependency-free (runs under `node --test`).
 */

export type PortVlanSource = "live" | "config" | "applied" | "intent" | "cli" | "none";
/** Where the DEVICE-SIDE membership of a port came from (OP#1819): the switch's own config read
 *  over CLI, the configuration of record owgw holds, or the state report (untagged VLAN only). */
export type ObservedSource = "cli" | "config" | "state" | "none";
export type ObservedConfidence = "high" | "medium" | "low" | "none";
export type PortVlanMode = "trunk" | "ap" | "access" | "excluded";

export interface PortVlanTagged {
  vlan: number;
  name: string | null;
  /** Live traffic seen on this VLAN on this port (MAC table). */
  seen: boolean;
  /** Meridian's site VLAN rollout intends it here. */
  intended: boolean;
  source: PortVlanSource;
}

export interface PortVlanDrift {
  code:
    | "native_mismatch"
    | "unexpected_vlan"
    | "missing_on_switch"
    | "missing_on_port"
    | "applied_not_on_switch"
    | "not_in_plan"
    | "not_confirmed";
  /**
   * drift = the switch proves a disagreement; unconfirmed = intended, nothing confirms it yet;
   * info = configured on the switch but not in Meridian's plan (a hand-set VLAN is not an error).
   */
  severity: "drift" | "unconfirmed" | "info";
  message: string;
  vlans: number[];
}

export interface PortVlanNeed {
  vlan: number;
  name: string | null;
  /** Who needs it, e.g. "JH-GW-01 (gateway)". */
  why: string;
  untagged?: boolean;
}

/**
 * What the DEVICE says a port carries (services/switch_port_vlans.observed_membership). `tagged`
 * null = the source cannot say (state report): UNKNOWN, never "nothing tagged".
 */
export interface PortVlanObserved {
  source: ObservedSource;
  native: number | null;
  untagged: number[];
  tagged: number[] | null;
  observed_at: string | null;
  age_seconds: number | null;
  confidence: ObservedConfidence;
  note: string | null;
}

/** Switch-level: can Meridian read this switch's VLANs, and when did it last? */
export interface DeviceReadSummary {
  method: "cli" | "state" | "none";
  /** ok / stale = a stored read; never_read = readable, not yet; partial = untagged only; unavailable = nothing. */
  status: "ok" | "stale" | "never_read" | "partial" | "unavailable";
  can_read: boolean;
  reason: string | null;
  observed_at: string | null;
  age_seconds: number | null;
  port_count: number | null;
  via: { gateway_serial: string | null; gateway_name: string | null } | null;
  last_attempt: { at: string | null; ok: boolean; error: string | null; code: string | null } | null;
}

export interface PortVlanView {
  port: string;
  label: string;
  mode: PortVlanMode;
  mode_reason: string;
  native: { vlan: number | null; name: string | null; source: PortVlanSource };
  tagged: PortVlanTagged[];
  tagged_source: PortVlanSource;
  /** false = neither the config nor Meridian's writes say anything: the list is UNKNOWN. */
  tagged_known: boolean;
  seen_vlans: number[];
  intent: {
    role: PortVlanMode | null;
    reason: string | null;
    tagged: number[];
    native: number | null;
    neighbors: { name: string | null; class: string | null; serial: string | null }[];
  } | null;
  applied_at: string | null;
  stp: { edge: boolean | null; role: string | null; state: string | null; edge_configured: boolean | null };
  needs: PortVlanNeed[];
  /** Device-side membership; absent from controllers older than OP#1819. */
  observed?: PortVlanObserved;
  drift: PortVlanDrift[];
}

export interface PortVlansResponse {
  serial: string;
  site_id: number | null;
  error?: string;
  sources: Partial<Record<"live" | "config" | "applied" | "intent" | "cli", {
    available: boolean;
    at?: string | null;
    age_seconds?: number | null;
    sessions?: number;
    tagged_membership?: boolean;
  }>>;
  vlan_database: number[];
  /** Where the VLAN list came from; "none" = not trustworthy here (SonicFi state lists SVIs only). */
  vlan_database_source?: "cli" | "state" | "none";
  device_read?: DeviceReadSummary;
  site_vlans: { vlan: number; name: string | null }[];
  ports: Record<string, PortVlanView>;
}

/** The server's before -> after explanation of a previewed change (switch/ports/apply dry run). */
export interface PortVlanDiff {
  port: string;
  before: { mode: PortVlanMode | null; native: number | null; tagged: number[] };
  after: { native: number | null; tagged: number[] };
  added: number[];
  removed: number[];
  native_change: { from: number | null; to: number | null } | null;
  warnings: string[];
  basis: PortVlanSource;
}

/** What the operator is editing. Starts as a copy of the port's current VLANs. */
export interface VlanEdit {
  mode: "trunk" | "access";
  /** Untagged VLAN (trunk native, or the access VLAN). "" = leave as is. */
  untagged: string;
  /** The tagged set the port should end with (trunk only). */
  tagged: number[];
}

const MODE_TEXT: Record<PortVlanMode, string> = {
  trunk: "Trunk",
  ap: "AP trunk",
  access: "Access",
  excluded: "Excluded",
};

export function modeLabel(mode: PortVlanMode | null | undefined): string {
  return mode ? MODE_TEXT[mode] : "Unknown";
}

const SOURCE_TEXT: Record<PortVlanSource, string> = {
  live: "switch report",
  config: "pushed config",
  applied: "Meridian's changes",
  intent: "Meridian's plan",
  cli: "the switch (read)",
  none: "no record",
};

export function sourceLabel(src: PortVlanSource | null | undefined): string {
  return SOURCE_TEXT[src ?? "none"];
}

/** "400 Network Management" or "400". */
export function vlanLabel(vlan: number, name?: string | null): string {
  return name ? `${vlan} ${name}` : String(vlan);
}

/** 42 -> "just now", 300 -> "5 min ago", 7200 -> "2 h ago", 3 days -> "3 d ago". */
export function fmtAge(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return "age unknown";
  if (seconds < 90) return "just now";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 172800) return `${Math.round(seconds / 3600)} h ago`;
  return `${Math.round(seconds / 86400)} d ago`;
}

/** True when the port carries (or is meant to carry) tagged VLANs -- the faceplate marker. */
export function isTrunkLike(v: PortVlanView | null | undefined): boolean {
  return !!v && (v.mode === "trunk" || v.mode === "ap");
}

/** "Trunk · untagged 1 · 5 tagged" -- header line and chip tooltip. */
export function vlanSummary(v: PortVlanView | null | undefined): string {
  if (!v) return "";
  const parts = [modeLabel(v.mode)];
  if (v.native.vlan != null) parts.push(`untagged ${v.native.vlan}`);
  if (v.tagged_known || v.tagged.length) parts.push(`${v.tagged.length} tagged`);
  else if (v.mode !== "access") parts.push("tagged not recorded");
  return parts.join(" · ");
}

/**
 * Drift items the switch PROVES (badge), vs merely unconfirmed intent and hand-set extras (muted
 * notes). `unconfirmed` keeps its old meaning; `info` is new with the device read (OP#1819).
 */
export function splitDrift(v: PortVlanView | null | undefined): {
  drift: PortVlanDrift[];
  unconfirmed: PortVlanDrift[];
  info: PortVlanDrift[];
} {
  const all = v?.drift ?? [];
  return {
    drift: all.filter((d) => d.severity === "drift"),
    unconfirmed: all.filter((d) => d.severity === "unconfirmed"),
    info: all.filter((d) => d.severity === "info"),
  };
}

// =========================================================================================
// Device read (OP#1819): the switch's own VLANs, and how much to trust what is shown
// =========================================================================================

const OBSERVED_TEXT: Record<ObservedSource, string> = {
  cli: "switch",
  config: "pushed config",
  state: "untagged only",
  none: "not reported",
};

/** Short source tag for a table cell: "switch" / "pushed config" / "untagged only" / "not reported". */
export function observedSourceLabel(src: ObservedSource | null | undefined): string {
  return OBSERVED_TEXT[src ?? "none"];
}

/** The site Ports table VLAN cell for one port. */
export interface VlanCell {
  /** "1" / "—". */
  native: string;
  /** Tagged VLAN ids the DEVICE reports; null = unknown (rendered as "tagged ?", never as none). */
  tagged: number[] | null;
  /** Source tag shown next to the numbers. */
  source: string;
  confidence: ObservedConfidence;
  /** Tooltip: source, age and any caveat, in full. */
  title: string;
  /** The switch disagrees with Meridian on this port. */
  drift: boolean;
}

/**
 * Build the VLAN cell from the port-vlans view, falling back to the port list's native VLAN when
 * the view has not loaded (or the controller predates OP#1819). Never invents a tagged list. PURE.
 */
export function vlanCell(view: PortVlanView | null | undefined, fallbackNative?: number | null): VlanCell {
  const obs = view?.observed;
  if (!obs) {
    const n = view?.native.vlan ?? fallbackNative ?? null;
    return {
      native: n != null ? String(n) : "—",
      tagged: null,
      source: n != null ? "untagged only" : "not reported",
      confidence: n != null ? "low" : "none",
      title: "Only the untagged VLAN is known for this port.",
      drift: false,
    };
  }
  const native = obs.native ?? view?.native.vlan ?? fallbackNative ?? null;
  const parts: string[] = [
    obs.source === "cli"
      ? `Read from the switch ${fmtAge(obs.age_seconds)}`
      : obs.source === "config"
        ? "Configuration of record held by owgw (pushed, not read back)"
        : obs.source === "state"
          ? "The switch reports its untagged VLAN only; tagged VLANs are unknown"
          : "The switch reports nothing for this port",
  ];
  if (obs.note && obs.source !== "state") parts.push(obs.note);
  parts.push(`Confidence: ${obs.confidence}.`);
  const { drift } = splitDrift(view);
  if (drift.length) parts.push(drift.map((d) => d.message).join(" "));
  return {
    native: native != null ? String(native) : "—",
    tagged: obs.tagged == null ? null : [...obs.tagged].sort((a, b) => a - b),
    source: observedSourceLabel(obs.source),
    confidence: obs.confidence,
    title: parts.join(" · "),
    drift: drift.length > 0,
  };
}

/**
 * Does this port carry `vlan` according to the DEVICE? true / false / null (= unknown). PURE.
 * The UI twin of services/switch_vlan_read.port_carries: a state-only port knows its untagged
 * VLAN and nothing else, so "is 1000 here?" is null there, never false.
 */
export function portCarries(view: PortVlanView | null | undefined, vlan: number): boolean | null {
  const obs = view?.observed;
  if (!obs) return view?.native.vlan === vlan ? true : null;
  if (obs.native === vlan || obs.untagged.includes(vlan)) return true;
  if (obs.tagged == null) return null;
  return obs.tagged.includes(vlan);
}

/**
 * One line for a switch: where its port VLANs come from and whether it can be read now. PURE.
 * The operator must never be left to assume a native-only view is complete.
 */
export function deviceReadLine(dr: DeviceReadSummary | null | undefined): { text: string; tone: "ok" | "muted" | "warn" } {
  if (!dr) return { text: "Tagged VLANs: source unknown (older controller).", tone: "muted" };
  const failed = dr.last_attempt && !dr.last_attempt.ok
    ? ` Last read attempt failed: ${dr.last_attempt.error ?? "unknown error"}.`
    : "";
  switch (dr.status) {
    case "ok":
      return {
        text: `VLANs read from the switch ${fmtAge(dr.age_seconds)}${dr.via?.gateway_name ? ` via ${dr.via.gateway_name}` : ""}.${failed}`,
        tone: failed ? "warn" : "ok",
      };
    case "stale":
      return { text: `VLANs last read from the switch ${fmtAge(dr.age_seconds)}; read again before relying on them.${failed}`, tone: "warn" };
    case "never_read":
      return { text: `${dr.reason ?? "Not read from the switch yet."}${failed}`, tone: failed ? "warn" : "muted" };
    case "partial":
      return { text: dr.reason ?? "Untagged VLAN only for this switch.", tone: "warn" };
    case "unavailable":
      return { text: `Port VLANs unavailable: ${dr.reason ?? "the switch reports nothing."}`, tone: "warn" };
    default:
      return { text: "Tagged VLANs: source unknown.", tone: "muted" };
  }
}

/** The editor's starting point: exactly what the port has now. */
export function editFromView(v: PortVlanView): VlanEdit {
  return {
    mode: isTrunkLike(v) || v.tagged.length > 0 ? "trunk" : "access",
    untagged: v.native.vlan != null ? String(v.native.vlan) : "",
    tagged: v.tagged.map((t) => t.vlan).sort((a, b) => a - b),
  };
}

/** A VLAN id 1..4094 from text, or null. */
export function parseVid(text: string): number | null {
  const t = text.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return n >= 1 && n <= 4094 ? n : null;
}

export interface VlanDelta {
  added: number[];
  removed: number[];
  nativeChange: { from: number | null; to: number } | null;
  /** Validation errors; the change must not be previewed while any exist. */
  errors: string[];
  /** Plain-language warnings (needed VLAN removed, trunk turned into access, ...). */
  warnings: string[];
}

/**
 * What the edit changes relative to the current VLANs. PURE.
 * Access mode ends with exactly one untagged VLAN: every current tagged VLAN is removed, and
 * the warning says so -- the "blind access overwrite" is now an explicit, listed removal.
 */
export function vlanDelta(v: PortVlanView, edit: VlanEdit): VlanDelta {
  const errors: string[] = [];
  const warnings: string[] = [];
  const before = new Set(v.tagged.map((t) => t.vlan));
  const curNative = v.native.vlan;
  let untagged: number | null = curNative;
  if (edit.untagged.trim() !== "") {
    untagged = parseVid(edit.untagged);
    if (untagged == null) errors.push("Untagged VLAN must be a number from 1 to 4094.");
  } else if (edit.mode === "access") {
    errors.push("An access port needs its VLAN.");
  }
  const after = new Set(edit.mode === "access" ? [] : edit.tagged);
  if (untagged != null) after.delete(untagged);
  const added = [...after].filter((x) => !before.has(x)).sort((a, b) => a - b);
  const removed = [...before].filter((x) => !after.has(x) && x !== untagged).sort((a, b) => a - b);
  const nativeChange = untagged != null && untagged !== curNative ? { from: curNative, to: untagged } : null;

  const needs = new Map(v.needs.map((n) => [n.vlan, n]));
  const lost = [...removed];
  if (nativeChange && curNative != null && !after.has(curNative)) lost.push(curNative);
  for (const x of lost) {
    const n = needs.get(x);
    if (n) warnings.push(`Removes VLAN ${vlanLabel(x, n.name)}, which ${n.why} on this port needs.`);
  }
  if (edit.mode === "access" && isTrunkLike(v)) {
    warnings.push(`Makes this ${modeLabel(v.mode).toLowerCase()} port an access port: ${removed.length} tagged VLAN${removed.length === 1 ? "" : "s"} come off.`);
  }
  if (nativeChange && isTrunkLike(v) && v.needs.length) {
    warnings.push(`Changes the untagged VLAN toward ${v.needs[0]!.why} from ${curNative ?? "unknown"} to ${untagged}.`);
  }
  if (!v.tagged_known && (edit.mode === "access" || removed.length)) {
    warnings.push("Tagged VLANs on this port are not recorded: any set by hand on the switch are not shown and are not removed.");
  }
  return { added, removed, nativeChange, errors, warnings };
}

/**
 * The structured change for the controller (services/switch_portconfig), carrying ONLY the delta.
 * Returns null when nothing VLAN-related changed. PURE.
 */
export function buildVlanChange(
  v: PortVlanView,
  edit: VlanEdit,
): { mode: "trunk" | "access"; access_vlan?: number; native_vlan?: number; previous_native_vlan?: number; tagged_vlans?: number[]; remove_vlans?: number[] } | null {
  const d = vlanDelta(v, edit);
  if (d.errors.length) return null;
  if (edit.mode === "access") {
    const vid = d.nativeChange?.to ?? v.native.vlan;
    if (vid == null) return null;
    const isAccessAlready = !isTrunkLike(v) && v.tagged.length === 0 && vid === v.native.vlan;
    if (isAccessAlready) return null;
    const remove = new Set(d.removed);
    // The outgoing untagged VLAN comes off too (the renderer strips VLAN 1 by itself).
    if (v.native.vlan != null && v.native.vlan !== vid && v.native.vlan !== 1) remove.add(v.native.vlan);
    const out: NonNullable<ReturnType<typeof buildVlanChange>> = { mode: "access", access_vlan: vid };
    if (remove.size) out.remove_vlans = [...remove].sort((a, b) => a - b);
    return out;
  }
  if (!d.added.length && !d.removed.length && !d.nativeChange) return null;
  const out: NonNullable<ReturnType<typeof buildVlanChange>> = { mode: "trunk" };
  if (d.added.length) out.tagged_vlans = d.added;
  if (d.removed.length) out.remove_vlans = d.removed;
  if (d.nativeChange) {
    out.native_vlan = d.nativeChange.to;
    if (d.nativeChange.from != null) out.previous_native_vlan = d.nativeChange.from;
  }
  return out;
}

/**
 * Edge-port selector initial value: the switch's live STP report (oper-edge-port) first, then
 * what Meridian last wrote. "" = unknown -> the selector offers "(unchanged)".
 */
export function edgeInitial(v: PortVlanView | null | undefined): "" | "on" | "off" {
  const e = v?.stp.edge ?? v?.stp.edge_configured;
  return e === true ? "on" : e === false ? "off" : "";
}

/**
 * The port's tagged VLANs NOW, or null when unknown. The device's own read (`observed`) first;
 * otherwise the view's tagged list only when the view says it is known (config / Meridian's
 * record). PURE.
 */
export function currentTaggedSet(view: PortVlanView | null | undefined): number[] | null {
  const obs = view?.observed;
  if (obs && obs.tagged != null) return [...obs.tagged].sort((a, b) => a - b);
  if (view?.tagged_known) return view.tagged.map((t) => t.vlan).sort((a, b) => a - b);
  return null;
}

/**
 * The tagged delta for the site PortEditor (OP#1819). PURE.
 *   current known, trunk:  field = desired set -> add = field - current, remove = current - field
 *   current known, access: every current tagged VLAN comes off (an access port has one VLAN)
 *   current unknown:       field = VLANs to add, nothing implicit is removed
 * The untagged VLAN is never in either list.
 */
export function taggedEditDelta(
  current: number[] | null,
  mode: "" | "access" | "trunk",
  field: number[],
  untagged: number | null,
): { add: number[]; remove: number[] } {
  const sort = (xs: Iterable<number>) => [...new Set(xs)].filter((v) => v !== untagged).sort((a, b) => a - b);
  if (!mode) return { add: [], remove: [] };
  if (current == null) return { add: mode === "trunk" ? sort(field) : [], remove: [] };
  if (mode === "access") return { add: [], remove: sort(current) };
  const cur = new Set(current);
  const want = new Set(field);
  return { add: sort(field.filter((v) => !cur.has(v))), remove: sort(current.filter((v) => !want.has(v))) };
}
